// Object type pages (/ontology/t/:id) and object views for Business Central records (/o/:entity/:key).
import { Link, Navigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox, Severity, StatePill, ago, label } from "../ui";
import { Icon } from "../icons";
import { bcUrl } from "../config";
import { EDGES, OBJECTS, VIA_LABEL, objectById } from "../ontology";
import { STATUS_LABEL, findArea, areaHref } from "../suite";
import { AREA_OF, INTERFACES, PROPS, actionsOn, cardOf, sourceOf } from "../ontologySchema";
import { ActionCard, TypeChip, typeHref } from "./OntologyPage";
import { anchorFor } from "../models";
import { AnchorCard } from "../RecordGroups";

export const objectHref = (entity: string, key: string) => `/o/${entity}/${encodeURIComponent(key)}`;

/** Open one BC record in Business Central: its list page filtered to the key. */
function bcRecordUrl(entity: string, key: string) {
  const src = sourceOf(entity);
  if (!src.bcPage) return null;
  const field = PROPS[entity]?.find((p) => p.key)?.name ?? "No.";
  return `${bcUrl(src.bcPage)}&filter=${encodeURIComponent(`'${field}' IS '${key}'`)}`;
}

function Crumbs({ items }: { items: [string, string?][] }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {items.map(([t, to], i) => <span key={t} style={{ display: "contents" }}>{i > 0 && <span>/</span>}{to ? <Link to={to}>{t}</Link> : <span>{t}</span>}</span>)}
    </nav>
  );
}

function LinkRows({ id }: { id: string }) {
  const out = EDGES.filter((e) => e.from === id), inc = EDGES.filter((e) => e.to === id);
  const row = (other: string, verb: string, e: (typeof EDGES)[number], dir: "out" | "in") => (
    <tr key={`${dir}${e.from}${e.to}${e.label}`}>
      <td>{dir === "out" ? <><span className="muted" style={{ fontStyle: "italic" }}>{verb}</span> <TypeChip id={other} /></> : <><TypeChip id={other} /> <span className="muted" style={{ fontStyle: "italic" }}>{verb} this</span></>}</td>
      <td className="muted">{cardOf(e)}</td>
      <td className="muted" style={{ fontSize: 12 }}>{VIA_LABEL[e.via]}</td>
      <td><span className={`status ${e.status === "live" ? "info" : ""}`}>{STATUS_LABEL[e.status]}</span></td>
    </tr>
  );
  return (
    <table className="dgrid">
      <thead><tr><th>Link</th><th>Cardinality</th><th>How it's made</th><th>Status</th></tr></thead>
      <tbody>{out.map((e) => row(e.to, e.label, e, "out"))}{inc.map((e) => row(e.from, e.label, e, "in"))}</tbody>
    </table>
  );
}

function ObjectsList({ id }: { id: string }) {
  const o = objectById(id);
  const isRecordType = id === "issue" || id === "capa";
  const { data, error } = useLoad(async () => {
    if (isRecordType) return { kind: "rec" as const, rows: await api.records(id as "issue" | "capa") };
    if (o.side === "bc") return { kind: "bc" as const, rows: (await api.erpList(o.bcEntity ?? id)).rows };
    return null;
  }, [id]);
  if (error) return <div className="card-body"><ErrorBox error={error} /></div>;
  if (!isRecordType && o.side !== "bc") return <p className="dash-empty" style={{ margin: 16 }}>No objects yet: this type arrives with {STATUS_LABEL[o.status]}.</p>;
  if (!data) return <p className="dash-empty dash-skeleton" style={{ margin: 16 }}>Loading</p>;
  if (!data.rows.length) return <p className="dash-empty" style={{ margin: 16 }}>{o.side === "bc" ? `No ${o.name.toLowerCase()} records are linked yet. Link one when you raise an issue.` : "None yet."}</p>;
  return data.kind === "rec" ? (
    <table className="dgrid">
      <thead><tr><th>Number</th><th>Title</th><th>Stage</th><th>Updated</th></tr></thead>
      <tbody>{data.rows.slice(0, 25).map((r) => <tr key={r.id}><td><Link to={`/r/${r.number}`} className="mono">{r.number}</Link></td><td>{r.title}</td><td><StatePill state={r.state} /></td><td className="muted">{ago(r.updatedAt)}</td></tr>)}</tbody>
    </table>
  ) : (
    <table className="dgrid">
      <thead><tr><th>{PROPS[id]?.find((p) => p.key)?.name ?? "Key"}</th><th style={{ textAlign: "right" }}>Linked records</th><th style={{ textAlign: "right" }}>Open</th><th>Last activity</th></tr></thead>
      <tbody>{data.rows.map((r) => <tr key={r.key}><td><Link to={objectHref(id, r.key)} className="mono">{r.key}</Link></td><td style={{ textAlign: "right" }}>{r.records}</td><td style={{ textAlign: "right" }}>{r.open}</td><td className="muted">{ago(r.lastAt)}</td></tr>)}</tbody>
    </table>
  );
}

export function ObjectTypePage() {
  const { id = "" } = useParams();
  const o = OBJECTS.find((x) => x.id === id);
  if (!o) return <div className="page"><div className="card empty">Unknown object type. <Link to="/ontology/types">All object types</Link></div></div>;
  const src = sourceOf(id);
  const area = findArea(AREA_OF[id] ?? "");
  const props = PROPS[id] ?? [];
  const ifaces = INTERFACES.filter((i) => i.types.includes(id));
  const actions = actionsOn(id);

  return (
    <>
      <CommandBar status="Object type">
        <Link className="cmd" to="/ontology/types">← Object types</Link>
        {src.bcPage && <a className="cmd" href={bcUrl(src.bcPage)} target="_blank" rel="noopener noreferrer">Open list in BC <Icon name="ext" size={12} /></a>}
        {src.route && <Link className="cmd" to={src.route}>Open in courageMFG</Link>}
      </CommandBar>
      <div className="page">
        <Crumbs items={[["Ontology", "/ontology"], ["Object types", "/ontology/types"], [o.name]]} />
        <header className="area-head">
          <span className="area-ico" style={{ background: o.side === "bc" ? "var(--ink)" : "var(--brand)" }}>{o.side === "bc" ? <b style={{ fontSize: 13 }}>BC</b> : <Icon name={area?.icon ?? "insights"} size={22} />}</span>
          <div>
            <h1 className="serif">{o.name}</h1>
            <p className="sub">{o.what}</p>
            <div className="btn-row" style={{ marginTop: 8, gap: 6 }}>
              <span className={`status ${o.status === "live" ? "info" : ""}`}>{STATUS_LABEL[o.status]}</span>
              {area && <Link to={areaHref(area)} className="bc-link">{area.name}</Link>}
              {ifaces.map((i) => <span key={i.id} className="bc-link" title={i.what}>{i.name}</span>)}
            </div>
          </div>
        </header>

        <div className="grid cols-main">
          <div className="stack">
            {anchorFor(id) && (
              <section>
                <div className="sec-h"><h2>Record page</h2><span className="muted">What each {o.name.toLowerCase()} shows, in three groups</span></div>
                <AnchorCard a={anchorFor(id)!} compact />
              </section>
            )}
            <section className="dash-card" style={{ padding: 0 }}>
              <div className="card-head"><h2>Properties</h2><span className="muted" style={{ fontSize: 12 }}>{props.length}</span></div>
              {props.length ? (
                <table className="dgrid">
                  <thead><tr><th>Property</th><th>Type</th><th>Notes</th></tr></thead>
                  <tbody>{props.map((p) => (
                    <tr key={p.name}>
                      <td style={{ fontWeight: 600 }}>{p.name} {p.key && <span className="status info">Key</span>} {p.title && <span className="status">Title</span>}</td>
                      <td className="muted">{p.type}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{p.note ?? ""}</td>
                    </tr>))}
                  </tbody>
                </table>
              ) : <p className="dash-empty" style={{ margin: 16 }}>Properties are defined when this type is built.</p>}
            </section>
            <section className="dash-card" style={{ padding: 0 }}>
              <div className="card-head"><h2>Link types</h2></div>
              <LinkRows id={id} />
            </section>
            <section className="dash-card" style={{ padding: 0 }}>
              <div className="card-head"><h2>Objects</h2><span className="muted" style={{ fontSize: 12 }}>{o.side === "bc" ? "BC records courageMFG links to" : "In courageMFG"}</span></div>
              <ObjectsList id={id} />
            </section>
          </div>
          <div className="stack">
            <section className="dash-card">
              <div className="dash-card-h">Data source</div>
              <dl className="action-dl" style={{ marginTop: 10 }}>
                <dt>System</dt><dd>{src.system === "Business Central" ? <><span className="tag bc">BC</span> Business Central</> : "courageMFG (Neon Postgres)"}</dd>
                <dt>Backed by</dt><dd>{src.detail}</dd>
                <dt>Writes</dt><dd>{o.side === "bc" ? "Read in courageMFG; changes go through action types that write back to BC" : "Only through action types, with full history"}</dd>
              </dl>
            </section>
            <section>
              <div className="sec-h"><h2>Action types</h2><span className="muted">{actions.length}</span></div>
              <div className="stack">
                {actions.length ? actions.map((a) => <ActionCard key={a.id} a={a} />) : <div className="dash-card dash-empty" style={{ margin: 0 }}>No actions change this type directly.</div>}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

export function ObjectView() {
  const { entity = "", key: rawKey = "" } = useParams();
  const key = decodeURIComponent(rawKey);
  const o = OBJECTS.find((x) => x.id === entity || x.bcEntity === entity);
  const isCourage = !!o && o.side === "courage";
  const { data, error } = useLoad(() => (isCourage ? Promise.resolve(null) : api.erpView(entity, key)), [entity, key]);
  if (isCourage) return <Navigate to={`/r/${key}`} replace />;
  const type = o ?? { id: entity, name: label(entity), what: "", side: "bc" as const, status: "live" as const };
  const props = PROPS[type.id] ?? [];
  const open = bcRecordUrl(type.id, key);
  const byEntity = (data?.neighbors ?? []).reduce<Record<string, { key: string; count: number }[]>>((m, n) => ((m[n.entity] ||= []).push(n), m), {});
  const openCount = data?.records.filter((r) => !["closed", "cancelled", "escalated"].includes(r.state)).length;

  return (
    <>
      <CommandBar status="Object view">
        <Link className="cmd primary" to={`/new?link=${encodeURIComponent(`${entity}:${key}`)}`}><Icon name="plus" size={14} />Raise issue on this {type.name.toLowerCase()}</Link>
        {open && <a className="cmd" href={open} target="_blank" rel="noopener noreferrer">Open in Business Central <Icon name="ext" size={12} /></a>}
        <Link className="cmd" to={typeHref(type.id)}>About {type.name.toLowerCase()}s</Link>
      </CommandBar>
      <div className="page">
        <Crumbs items={[["Ontology", "/ontology"], [type.name, typeHref(type.id)], [key]]} />
        <header className="area-head">
          <span className="area-ico"><b style={{ fontSize: 13 }}>BC</b></span>
          <div>
            <span className="dash-label">{type.name}</span>
            <h1 className="serif mono-ish">{key}</h1>
            <p className="sub">{data ? (data.records.length ? `${data.records.length} courageMFG record${data.records.length === 1 ? "" : "s"} linked, ${openCount} open.` : "No courageMFG records are linked to this yet.") : " "}</p>
          </div>
        </header>
        <ErrorBox error={error} />

        <div className="grid cols-main">
          <div className="stack">
            {(() => {
              const done = ["closed", "cancelled", "escalated"];
              const active = data?.records.filter((r) => !done.includes(r.state)) ?? [];
              const history = data?.records.filter((r) => done.includes(r.state)) ?? [];
              const table = (rows: typeof active) => rows.length ? (
                <table className="dgrid">
                  <thead><tr><th>Number</th><th>Title</th><th>Severity</th><th>Stage</th><th>Updated</th></tr></thead>
                  <tbody>{rows.map((r) => (
                    <tr key={r.id}><td><Link to={`/r/${r.number}`} className="mono">{r.number}</Link></td><td>{r.title}</td><td><Severity severity={r.severity} /></td><td><StatePill state={r.state} /></td><td className="muted">{ago(r.updatedAt)}</td></tr>
                  ))}</tbody>
                </table>
              ) : <p className="dash-empty" style={{ margin: "8px 16px 16px" }}>{data ? "None." : ""}</p>;
              const anchor = anchorFor(type.id);
              return (
                <>
                  <section className="dash-card anchor-group related" style={{ background: "var(--surface)" }}>
                    <div className="ag-h">Related records</div>
                    <div className="dash-cap">Other BC records the same issues and CAPAs point to{anchor ? `; in BC also: ${anchor.related.map((n) => n.name.toLowerCase()).join(", ")}` : ""}</div>
                    {Object.keys(byEntity).length ? (
                      <div className="neighbors">
                        {Object.entries(byEntity).map(([ent, ns]) => (
                          <div key={ent} className="nb-group">
                            <TypeChip id={OBJECTS.find((x) => x.bcEntity === ent || x.id === ent)?.id ?? ent} />
                            <div className="nb-keys">{ns.map((n) => <Link key={n.key} to={objectHref(ent, n.key)} className="bc-link"><span className="mono">{n.key}</span><span className="muted">{n.count}</span></Link>)}</div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="dash-empty">{data ? "None yet." : ""}</p>}
                  </section>
                  <section className="dash-card anchor-group active" style={{ background: "var(--surface)", padding: 0 }}>
                    <div className="card-head" style={{ border: 0 }}><div><div className="ag-h">Active</div><div className="dash-cap">Open issues and CAPAs on this {type.name.toLowerCase()}</div></div><span className="muted" style={{ fontSize: 12 }}>{data ? active.length : ""}</span></div>
                    {table(active)}
                  </section>
                  <section className="dash-card anchor-group history" style={{ background: "var(--surface)", padding: 0 }}>
                    <div className="card-head" style={{ border: 0 }}><div><div className="ag-h">History</div><div className="dash-cap">Closed and escalated{open ? "; posted BC history opens in Business Central" : ""}</div></div><span className="muted" style={{ fontSize: 12 }}>{data ? history.length : ""}</span></div>
                    {table(history)}
                  </section>
                </>
              );
            })()}
          </div>
          <div className="stack">
            <section className="dash-card">
              <div className="dash-card-h">Properties</div>
              <div className="dash-cap">Values come from Business Central once it's connected</div>
              <dl className="action-dl" style={{ marginTop: 10 }}>
                {props.map((p) => <span key={p.name} style={{ display: "contents" }}><dt>{p.name}</dt><dd className={p.key ? "mono" : "muted"}>{p.key ? key : "—"}</dd></span>)}
              </dl>
            </section>
            <section>
              <div className="sec-h"><h2>Actions</h2></div>
              <div className="stack">{actionsOn(type.id).map((a) => <ActionCard key={a.id} a={a} />)}</div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
